import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import axios from 'axios';
import { ArrowLeft, Trophy, Medal, Coins, Radio } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Leaderboard = () => {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/quiz-pools/${poolId}/leaderboard`);
      setLeaderboard(data);
    } catch (error) {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [poolId]);

  // WebSocket connection for live updates
  const connectWs = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const wsUrl = API_URL.replace(/^http/, 'ws') + `/api/ws/leaderboard/${poolId}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setWsConnected(true);
      // Send periodic pings to keep alive
      wsRef.current._pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'leaderboard_update' && msg.data) {
          setLeaderboard(msg.data);
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      if (wsRef.current?._pingInterval) clearInterval(wsRef.current._pingInterval);
      // Reconnect after 3 seconds
      reconnectTimer.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, [poolId]);

  useEffect(() => {
    fetchLeaderboard();
    connectWs();

    return () => {
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [fetchLeaderboard, connectWs]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
            <p className="mt-4 text-[#6B7280]">Loading leaderboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const getRankBadge = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-600" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-500" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-orange-500" />;
    return <span className="text-sm font-bold text-[#6B7280]">#{rank}</span>;
  };

  const getRowStyle = (rank) => {
    if (rank === 1) return 'leaderboard-gold border-yellow-300';
    if (rank === 2) return 'leaderboard-silver border-gray-200';
    if (rank === 3) return 'leaderboard-bronze border-orange-200';
    return 'bg-white border-slate-100';
  };

  return (
    <div className="min-h-screen" data-testid="leaderboard-page">
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/quiz-pools')}
            className="flex items-center gap-2 text-[#6B7280] hover:text-[#0EA5E9] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Quiz Pools</span>
          </button>

          {/* Live indicator */}
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
              wsConnected
                ? 'bg-green-100 text-green-700'
                : 'bg-slate-100 text-slate-500'
            }`}
            data-testid="live-indicator"
          >
            <Radio className={`w-3.5 h-3.5 ${wsConnected ? 'animate-pulse' : ''}`} />
            {wsConnected ? 'LIVE' : 'Connecting...'}
          </div>
        </div>

        <div className="text-center mb-12">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/fa278b8c-4730-4f77-ac2c-c0cda1085fb1/images/9d69229f0b24fcab0a8a8b9c0b56d1e073dd34303b464e3c81590b4f93836613.png"
            alt="Trophy"
            className="w-32 h-32 mx-auto object-contain pulse-glow mb-6"
          />
          <h1
            className="text-4xl sm:text-5xl font-black text-[#111827] mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Leaderboard
          </h1>
          <p className="text-base text-[#6B7280]">
            Dream11-style prize distribution - Top 50% win!
          </p>
        </div>

        {leaderboard.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#6B7280] text-lg">No submissions yet. Be the first to complete the quiz!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leaderboard-table">
            {leaderboard.map((entry) => (
              <div
                key={entry.rank}
                className={`${getRowStyle(entry.rank)} p-5 rounded-2xl border-2 shadow-sm transition-all hover:shadow-md`}
                data-testid={`leaderboard-row-${entry.rank}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center">
                      {getRankBadge(entry.rank)}
                    </div>
                    <div>
                      <p className="font-bold text-[#111827] text-lg">
                        {entry.user_name}
                        {entry.is_current_user && (
                          <span className="ml-2 text-xs bg-[#0EA5E9] text-white px-2 py-1 rounded-full">
                            You
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-[#6B7280]">
                        Time: {entry.time_taken}s
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Score */}
                    <div className="text-right">
                      <p className="text-2xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                        {entry.score}/{entry.total}
                      </p>
                      <p className="text-xs text-[#6B7280]">Score</p>
                    </div>

                    {/* Prize Won */}
                    {entry.prize > 0 && (
                      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] rounded-xl">
                        <Coins className="w-4 h-4 text-white" />
                        <span className="font-black text-white text-lg" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                          +{entry.prize.toLocaleString()}
                        </span>
                      </div>
                    )}

                    {entry.prize === 0 && (
                      <div className="px-4 py-2 bg-slate-100 rounded-xl">
                        <span className="font-medium text-[#6B7280] text-sm">No Prize</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-3 bg-[#FBBF24] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(245,158,11)] active:translate-y-1 active:shadow-none transition-all"
            data-testid="dashboard-button"
          >
            Back to Dashboard
          </button>
        </div>
      </main>
    </div>
  );
};

export default Leaderboard;
